import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/apiClient'
import type { components } from '../lib/api-types'

type CreateRequest = components['schemas']['ProjectCreateRequest']
type SyncResult = components['schemas']['SyncResult']

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateRequest) => {
      const { data, error, response } = await api.POST('/v1/projects', { body })
      // Carry the HTTP status so the form can distinguish a duplicate (409) from
      // a generic failure and show the right message.
      if (error || !data) {
        throw Object.assign(new Error('Failed to create project'), { status: response?.status })
      }
      return data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
  })
}

export function useSyncProject(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { content: string; commitSha?: string }): Promise<SyncResult> => {
      const { data, error } = await api.POST('/v1/projects/{id}/sync', {
        params: { path: { id } },
        body: { content: vars.content, ...(vars.commitSha ? { commitSha: vars.commitSha } : {}) },
      })
      if (error || !data) throw new Error('Sync failed')
      return data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
  })
}
